import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError, type SessionSummary } from '../api';

export default function History() {
  const [items, setItems] = useState<SessionSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = async (next: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.listSessions({ cursor: next, limit: 20 });
      setItems((prev) => (next === null ? page.items : [...prev, ...page.items]));
      setCursor(page.next_cursor);
      if (page.next_cursor === null) setDone(true);
    } catch (e) {
      setError(formatApiError(e, '載入歷史失敗，請稍後再試。'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="mp-display text-4xl text-tarot-gold mb-8">歷史</h1>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      {items.length === 0 && !loading && (
        <div className="card text-center text-tarot-muted">
          還沒有任何抽牌紀錄。
          <Link to="/reading" className="block mt-3 text-tarot-gold">開始第一次 →</Link>
        </div>
      )}

      <ul className="space-y-3">
        {items.map((s, i) => (
          <li
            key={s.session_id}
            style={{ ['--list-delay' as string]: `${Math.min(i, 12) * 0.05}s` }}
            className="list-enter"
          >
            <a
              href={`/reading?session=${s.session_id}`}
              className="card block hover:border-tarot-purple no-underline"
            >
              <div className="flex justify-between items-baseline">
                <span className="text-tarot-text truncate">
                  {s.intention || '（無提問描述）'}
                </span>
                <span className="text-xs text-tarot-muted whitespace-nowrap ml-3">
                  {new Date(s.created_at).toLocaleDateString('zh-TW')}
                </span>
              </div>
              <div className="text-xs text-tarot-muted mt-2">{statusLabel(s.status)}</div>
            </a>
          </li>
        ))}
      </ul>

      {!done && items.length > 0 && (
        <div className="text-center mt-8">
          <button
            onClick={() => load(cursor)}
            disabled={loading}
            className="btn-ghost"
          >
            {loading ? '載入中…' : '載入更多'}
          </button>
        </div>
      )}
    </div>
  );
}

function statusLabel(s: string): string {
  return (
    {
      awaiting: '等待中',
      reading: '進行中',
      reflection: '反思中',
      complete: '已完成',
      expired: '已過期',
    }[s] ?? s
  );
}
